import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { differenceInMinutes } from "date-fns";

import { useInterval } from "../../../../utils/hooks/useInterval";

import {
  getInstagramCatalogStatus,
  isStaleCatalogCursorError,
  refreshInstagramCatalog,
  searchInstagramCatalogProducts,
  type IgCatalogAccountSyncStatus,
  type IgCatalogProduct,
  type IgProductPickerResult,
} from "../../../../api/post";

/**
 * SKE-4543. The Instagram Shop catalog behind the product tagger.
 *
 * Two things here are worth knowing before changing anything.
 *
 * A product tag stores its `product_id` in the tag's `value` field:
 * `mapProductTags` in
 * `apps/schedugram-server/src/app/services/instagram-publishing/request.ts`
 * reads `productId: String(tag.value)`. The tag schema carries nothing else, so
 * the picker writes the id and resolves the name for display from the catalog it
 * already loaded. The legacy free-text flow writes a name or SKU into the same
 * field, which is what the phone path reads, and both flows stay reachable.
 *
 * Only an `approved` product renders on a published post. Meta accepts a tag on
 * one that is not approved without any error and the tag simply never appears.
 * A live read lists a non-approved product disabled with its reason; the synced
 * copy (SKE-5088) returns approved products only and says how many it left out.
 */

/** The single status Instagram renders on a published post. */
const APPROVED = "approved";

const REVIEW_STATUS_REASONS: Record<string, string> = {
  pending: "Awaiting Instagram's review",
  rejected: "Rejected by Instagram",
  outdated: "Out of date in your catalog",
};

const UNAPPROVED_REASON = "Not approved by Instagram";

export const UNNAMED_PRODUCT_LABEL = "Unnamed product";

export function productLabel(product: IgCatalogProduct): string {
  return (
    product.productName?.trim() ||
    product.retailerId?.trim() ||
    product.productId?.trim() ||
    UNNAMED_PRODUCT_LABEL
  );
}

/** Whether Sked will let this product be tagged. */
export function isTaggableProduct(product: IgCatalogProduct): boolean {
  return Boolean(product.productId) && product.reviewStatus === APPROVED;
}

/**
 * Why a product cannot be tagged, or undefined when it can.
 *
 * A status Meta sent that is none of its four documented values arrives as
 * `unknownReviewStatus`, and is treated as not approved, because approved is the
 * only value that publishes.
 */
export function productBlockedReason(
  product: IgCatalogProduct
): string | undefined {
  if (isTaggableProduct(product)) return undefined;
  if (!product.productId) return "Instagram sent no id for this product";
  return REVIEW_STATUS_REASONS[product.reviewStatus ?? ""] ?? UNAPPROVED_REASON;
}

export type ProductCatalogState =
  | { kind: "off" }
  | { kind: "loading" }
  | {
      kind: "ready";
      products: IgCatalogProduct[];
      after?: string;
      busy: boolean;
      /** A new search is running over the products still on screen. */
      searching?: boolean;
      /** A later request failed; the products above are still usable. */
      error?: string;
    }
  | {
      kind: "unavailable";
      verdict?: IgProductPickerResult;
      serverMessage?: string;
    };

export const joinNames = (names: string[]): string =>
  names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

const blamedAccounts = (
  accountIds: string[] | undefined,
  accountNames: Record<string, string>
): string => {
  const names = (accountIds ?? [])
    .map((accountId) => accountNames[accountId])
    .filter((name): name is string => Boolean(name));

  if (names.length > 0) return joinNames(names);
  return (accountIds?.length ?? 0) > 1
    ? "the selected accounts"
    : "the selected account";
};

export const COMMERCE_MANAGER_URL = "https://business.facebook.com/commerce/";

const NOT_ELIGIBLE_MESSAGE =
  "This Instagram account is not approved for product tagging yet. Check your shop's product tagging approval in Commerce Manager.";

export const EMPTY_CATALOG_MESSAGE =
  "Your Instagram catalog has no products yet. Add them in Commerce Manager.";

export const NONE_APPROVED_MESSAGE =
  "None of your products are approved for tagging yet.";

/**
 * What to tell the customer when the catalog picker cannot be shown, or null
 * when there is nothing to say beyond the free-text instructions.
 *
 * Every one of these leaves the free-text flow in place, so none of them is a
 * refusal. They explain why the product list is missing.
 */
export function catalogUnavailableMessage(
  state: ProductCatalogState,
  accountNames: Record<string, string>
): string | null {
  if (state.kind !== "unavailable") return null;
  if (state.serverMessage) return state.serverMessage;

  switch (state.verdict?.status) {
    case "not_eligible":
      return NOT_ELIGIBLE_MESSAGE;
    case "no_catalog":
      return `We could not find a product catalog for ${blamedAccounts(
        state.verdict.accountIds,
        accountNames
      )}. Check the Instagram Shop is linked to a catalog in Commerce Manager.`;
    case "catalog_mismatch":
      return "The selected accounts use different product catalogs, so there is no single product list to show. Tag them one account at a time to pick from a catalog.";
    default:
      return "We could not reach your Instagram catalog just now.";
  }
}

export const ACCOUNT_DISCONNECTED_MESSAGE =
  "This Instagram account is no longer connected. Reconnect it in Sked Social.";

/** The account check's refusals: 403 carries its own sentence, 404 means the account is gone. */
const reconnectSentence = (error: unknown): string | undefined => {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { error?: unknown; message?: unknown };
      };
    }
  )?.response;

  if (response?.status === 404 && response.data?.error === true)
    return ACCOUNT_DISCONNECTED_MESSAGE;
  if (response?.status !== 403) return undefined;
  return typeof response.data?.message === "string"
    ? response.data.message
    : undefined;
};

/** One account's catalog sync, as the status line reads it. */
export type CatalogSyncStatus = Pick<
  IgCatalogAccountSyncStatus,
  | "inProgress"
  | "productsSoFar"
  | "lastSyncedAt"
  | "cooldownUntil"
  | "failureMessage"
> & { accountId?: string };

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

/** "40 min ago", "3 hr ago", "2 days ago". */
function formatSyncedAgo(at: Date, now: Date): string {
  const minutes = Math.max(0, differenceInMinutes(now, at));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${plural(Math.floor(hours / 24), "day")} ago`;
}

/** "2 hr 20 min", "40 min", "under a minute". */
function formatWait(until: Date, now: Date): string {
  const minutes = Math.ceil((until.getTime() - now.getTime()) / 60_000);
  if (minutes < 1) return "under a minute";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

const parseDate = (value: string | undefined): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/**
 * The status line's sentence for one account, whether Refresh is offered, and
 * whether the sentence reports a failed sync. A failed sync also carries the
 * server's own reason as `detail`, shown on a line of its own.
 *
 * The failure is not dated: the status route returns when the last GOOD sync
 * ran, not when the failed one did, so the sentence dates the good one.
 *
 * Refresh is offered as a button only when it can run. During a cooldown the
 * sentence says when it comes back, failed or not, so a disabled button beside
 * it would say Refresh twice.
 *
 * The cooldown sentence dates the refresh from `lastSyncedAt`, because the
 * status route does not return when the manual refresh was requested.
 */
export function describeCatalogStatus(
  status: CatalogSyncStatus,
  now: Date
): {
  message: string;
  detail?: string;
  canRefresh: boolean;
  failed: boolean;
} {
  if (status.inProgress) {
    return {
      message:
        typeof status.productsSoFar === "number"
          ? `Syncing your catalog, ${plural(status.productsSoFar, "product")} so far`
          : "Syncing your catalog",
      canRefresh: false,
      failed: false,
    };
  }

  const lastSyncedAt = parseDate(status.lastSyncedAt);
  const cooldownUntil = parseDate(status.cooldownUntil);
  const onCooldown = Boolean(cooldownUntil && cooldownUntil > now);
  const available =
    onCooldown && cooldownUntil
      ? `Refresh available in ${formatWait(cooldownUntil, now)}`
      : null;

  if (status.failureMessage !== undefined) {
    const failedMessage = lastSyncedAt
      ? `Sync failed. Last synced ${formatSyncedAgo(lastSyncedAt, now)}.`
      : "Sync failed.";
    return {
      message: available ? `${failedMessage} ${available}.` : failedMessage,
      ...(status.failureMessage.trim()
        ? { detail: status.failureMessage.trim() }
        : {}),
      canRefresh: !onCooldown,
      failed: true,
    };
  }

  if (available) {
    return {
      message: lastSyncedAt
        ? `Synced ${formatSyncedAgo(lastSyncedAt, now)}. ${available}.`
        : available,
      canRefresh: false,
      failed: false,
    };
  }

  return {
    message: lastSyncedAt
      ? `Synced ${formatSyncedAgo(lastSyncedAt, now)}`
      : "Not synced yet",
    canRefresh: true,
    failed: false,
  };
}

const REFRESH_FAILED_MESSAGE =
  "We could not start the refresh. Try again in a moment.";
const REFRESH_DISABLED_MESSAGE =
  "Refreshing products is not available for this account.";
const REFRESH_RECONNECT_MESSAGE =
  "Reconnect this Instagram account in Sked Social to update your products.";
export const CATALOG_REQUEST_FAILED_MESSAGE =
  "We could not load products just now. Try again in a moment.";
const STATUS_FAILED_MESSAGE =
  "We could not check on your catalog sync. Close and reopen this window to check again.";

const STATUS_POLL_MS = 5000;
const CLOCK_TICK_MS = 60_000;

type SyncMeta = {
  source?: IgProductPickerResult["source"];
  syncedAt?: string;
  hiddenCount?: number;
  syncFailureMessage?: string;
};

const updateAccount = (
  current: CatalogSyncStatus[] | null,
  accountId: string,
  patch: Partial<CatalogSyncStatus>
): CatalogSyncStatus[] => {
  const rows = current ?? [];
  return rows.some((row) => row.accountId === accountId)
    ? rows.map((row) =>
        row.accountId === accountId ? { ...row, ...patch } : row
      )
    : [...rows, { accountId, inProgress: false, ...patch }];
};

/**
 * Loads the shared catalog once, then re-runs on each search.
 *
 * No accounts means no call at all: the route needs a publish-capable account,
 * and asking without one would answer 403 and put a reconnect notice in front of
 * a customer whose accounts all publish from a phone.
 *
 * SKE-5090. The sync status is read only once the products route has answered
 * from the synced copy, or a refresh was accepted, and it is polled only while a
 * sync is running. When the running sync finishes, the current search runs
 * again so the fresh rows replace the ones on screen.
 */
export function useProductCatalog(accountIds: string[]) {
  const accountKey = accountIds.join(",");
  const [state, setState] = useState<ProductCatalogState>(() =>
    accountKey ? { kind: "loading" } : { kind: "off" }
  );
  const [syncMeta, setSyncMeta] = useState<SyncMeta>({});
  const [accountStatuses, setAccountStatuses] = useState<
    CatalogSyncStatus[] | null
  >(null);
  const [pollArmed, setPollArmed] = useState(false);
  const [pollNonce, setPollNonce] = useState(0);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());
  /*
   * Every response checks it is still the latest before writing. A slow first
   * page landing after a fast search would otherwise replace the results the
   * customer is looking at with the ones they have already typed past.
   */
  const requestRef = useRef(0);
  const queryRef = useRef("");
  const stateRef = useRef(state);
  stateRef.current = state;
  const wasRunningRef = useRef(false);

  const search = useCallback(
    async (query: string) => {
      queryRef.current = query;
      if (!accountKey) {
        setState({ kind: "off" });
        return;
      }

      const sequence = ++requestRef.current;
      setState((current) =>
        current.kind === "ready"
          ? { ...current, busy: true, searching: true }
          : { kind: "loading" }
      );

      try {
        const result = await searchInstagramCatalogProducts({
          accountIds: accountKey.split(","),
          query,
        });
        if (sequence !== requestRef.current) return;

        setSyncMeta({
          source: result?.source,
          syncedAt: result?.syncedAt,
          hiddenCount: result?.hiddenCount,
          syncFailureMessage: result?.syncFailureMessage,
        });
        setNow(new Date());
        if (result?.source === "synced") setPollArmed(true);

        setState(
          result?.status === "ok"
            ? {
                kind: "ready",
                products: result.products ?? [],
                after: result.after,
                busy: false,
              }
            : { kind: "unavailable", verdict: result }
        );
      } catch (error) {
        if (sequence !== requestRef.current) return;
        const shown = stateRef.current;
        if (shown.kind === "ready") {
          setState({
            kind: "ready",
            products: shown.products,
            after: shown.after,
            busy: false,
            error: reconnectSentence(error) ?? CATALOG_REQUEST_FAILED_MESSAGE,
          });
          return;
        }
        setSyncMeta({});
        setState({
          kind: "unavailable",
          serverMessage: reconnectSentence(error),
        });
      }
    },
    [accountKey]
  );

  const searchRef = useRef(search);
  searchRef.current = search;

  const loadMore = useCallback(async () => {
    if (state.kind !== "ready" || !state.after || state.busy) return;

    const sequence = ++requestRef.current;
    const { after, products } = state;
    setState({ kind: "ready", products, after, busy: true });

    try {
      const result = await searchInstagramCatalogProducts({
        accountIds: accountKey.split(","),
        query: queryRef.current,
        after,
      });
      if (sequence !== requestRef.current) return;
      if (result?.status !== "ok") {
        setState({
          kind: "ready",
          products,
          after,
          busy: false,
          error: CATALOG_REQUEST_FAILED_MESSAGE,
        });
        return;
      }

      setState({
        kind: "ready",
        products: [...products, ...(result.products ?? [])],
        after: result.after,
        busy: false,
      });
    } catch (error) {
      if (sequence !== requestRef.current) return;
      if (isStaleCatalogCursorError(error)) {
        // The catalog re-synced under this cursor: page one of the new sync replaces the list.
        searchRef.current(queryRef.current);
        return;
      }
      setState({
        kind: "ready",
        products,
        after,
        busy: false,
        error: reconnectSentence(error) ?? CATALOG_REQUEST_FAILED_MESSAGE,
      });
    }
  }, [accountKey, state]);

  useEffect(() => {
    search("");
  }, [search]);

  useEffect(() => {
    if (!pollArmed || !accountKey) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const readStatus = async () => {
      try {
        const result = await getInstagramCatalogStatus(accountKey.split(","));
        if (cancelled) return;
        const statuses: CatalogSyncStatus[] = result?.accounts ?? [];
        setAccountStatuses(statuses);
        setNow(new Date());
        const running = statuses.some((status) => status.inProgress);
        if (wasRunningRef.current && !running) {
          searchRef.current(queryRef.current);
        }
        wasRunningRef.current = running;
      } catch (error) {
        if (cancelled) return;
        wasRunningRef.current = false;
        setRefreshError(reconnectSentence(error) ?? STATUS_FAILED_MESSAGE);
      }
      if (!cancelled && wasRunningRef.current) {
        timer = setTimeout(readStatus, STATUS_POLL_MS);
      }
    };

    readStatus();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pollArmed, pollNonce, accountKey]);

  const synced = syncMeta.source === "synced";

  useInterval(() => setNow(new Date()), synced ? CLOCK_TICK_MS : null);

  const refresh = useCallback(async (accountId: string) => {
    setRefreshBusy(true);
    setRefreshError(null);
    try {
      const result = await refreshInstagramCatalog(accountId);
      setNow(new Date());
      const status = result?.status;
      if (status === "queued" || status === "in_progress") {
        setAccountStatuses((current) =>
          updateAccount(current, accountId, {
            inProgress: true,
            productsSoFar: undefined,
          })
        );
        wasRunningRef.current = true;
        setPollArmed(true);
        setPollNonce((nonce) => nonce + 1);
      } else if (status === "cooldown") {
        setAccountStatuses((current) =>
          updateAccount(current, accountId, { cooldownUntil: result.retryAt })
        );
      } else if (status === "disabled") {
        setRefreshError(
          result.reason === "reconnect"
            ? REFRESH_RECONNECT_MESSAGE
            : REFRESH_DISABLED_MESSAGE
        );
      } else {
        setRefreshError(REFRESH_FAILED_MESSAGE);
      }
    } catch (error) {
      const refused =
        (error as { response?: { status?: number } })?.response?.status === 403;
      setRefreshError(
        reconnectSentence(error) ??
          (refused ? REFRESH_RECONNECT_MESSAGE : REFRESH_FAILED_MESSAGE)
      );
    } finally {
      setRefreshBusy(false);
    }
  }, []);

  const statuses = useMemo((): CatalogSyncStatus[] => {
    if (!synced) return [];
    if (accountStatuses && accountStatuses.length > 0) return accountStatuses;
    const ids = accountKey ? accountKey.split(",") : [];
    return [
      {
        accountId: ids.length === 1 ? ids[0] : undefined,
        inProgress: false,
        lastSyncedAt: syncMeta.syncedAt,
        failureMessage: syncMeta.syncFailureMessage,
      },
    ];
  }, [synced, accountStatuses, accountKey, syncMeta]);

  return {
    state,
    search,
    loadMore,
    sync: {
      synced,
      hiddenCount: synced ? syncMeta.hiddenCount : undefined,
      statuses,
      now,
      refresh,
      refreshBusy,
      refreshError,
    },
  };
}

export type CatalogSync = ReturnType<typeof useProductCatalog>["sync"];
